import type { ColumnDef, ColumnSizingState } from '@tanstack/react-table'
import type { Account } from '@/types'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { AccountHealthBadge } from '@/components/AccountHealthScore'

interface AccountsColumnsProps {
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: () => void
  allSelected?: boolean
  onDeleteAccount?: (accountId: string, name: string) => void
}

export const createAccountsColumns = ({
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
  onDeleteAccount,
}: AccountsColumnsProps = {}): ColumnDef<Account>[] => [
  {
    id: 'select',
    header: () => (
      <Checkbox
        checked={allSelected}
        onCheckedChange={onToggleSelectAll}
        aria-label="Select all accounts"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={selectedIds?.has(row.original.id) ?? false}
        onCheckedChange={() => onToggleSelect?.(row.original.id)}
        aria-label={`Select account ${row.original.name}`}
      />
    ),
    size: 50,
    minSize: 50,
    maxSize: 50,
    enableResizing: false,
  },
  {
    id: 'name',
    header: 'Account Name',
    accessorKey: 'name',
    meta: { sortField: 'name' },
    size: 250,
    cell: ({ row }) => (
      <div className="flex items-center gap-2 min-w-0">
        <Link
          to={`/accounts/${row.original.id}`}
          className="font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 truncate"
        >
          {row.original.name}
        </Link>
        {row.original.has_task === false && (
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 bg-[#941052] text-white border-[#941052] whitespace-nowrap"
          >
            No Task
          </Badge>
        )}
      </div>
    ),
  },
  {
    id: 'industry',
    header: 'Industry',
    accessorKey: 'industry',
    meta: { sortField: 'industry' },
    size: 180,
  },
  {
    id: 'type',
    header: 'Type',
    accessorKey: 'type',
    meta: { sortField: 'type' },
    size: 150,
  },
  {
    id: 'phone',
    header: 'Phone',
    accessorKey: 'phone',
    size: 180,
  },
  {
    id: 'website',
    header: 'Website',
    accessorKey: 'website',
    size: 200,
    cell: ({ row }) =>
      row.original.website ? (
        <a
          href={
            row.original.website.startsWith('http')
              ? row.original.website
              : `https://${row.original.website}`
          }
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
          aria-label={`Visit ${row.original.name} website`}
        >
          {row.original.website}
        </a>
      ) : null,
  },
  {
    id: 'owner_name',
    header: 'Owner',
    accessorKey: 'owner_name',
    meta: { sortField: 'owner_name' },
    size: 150,
    cell: ({ row }) => (
      <span className="truncate block max-w-[150px]">
        {(row.original as any).owner_name || '-'}
      </span>
    ),
  },
  {
    id: 'health',
    header: 'Health',
    accessorKey: 'health_score',
    size: 80,
    cell: ({ row }) => <AccountHealthBadge score={row.original.health_score} />,
  },
  {
    id: 'actions',
    header: () => <div className="text-right">Actions</div>,
    size: 160,
    minSize: 120,
    maxSize: 220,
    enableResizing: false,
    cell: ({ row }) => (
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" aria-label={`View account ${row.original.name}`} asChild>
          <Link to={`/accounts/${row.original.id}`}>View</Link>
        </Button>
        {onDeleteAccount && (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Delete account ${row.original.name}`}
            onClick={() => onDeleteAccount(row.original.id, row.original.name ?? '')}
          >
            Delete
          </Button>
        )}
      </div>
    ),
  },
]

export const accountsDefaultColumnSizing: ColumnSizingState = {
  select: 50,
  name: 250,
  industry: 180,
  type: 150,
  phone: 180,
  website: 200,
  owner_name: 150,
  health: 80,
  actions: 100,
}
